'use client'

import { useMemo, useState } from 'react'

import { Search } from 'lucide-react'

import { useUserTitles } from '../../hooks/useUserTitles'

import { useModalContext } from '@/shared/contexts/Modal'
import { cn } from '@/shared/lib/utils'
import { Button } from '@/shared/ui/Button'
import { inputClassName } from '@/shared/ui/input-styles'
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle
} from '@/shared/ui/Modal'

interface UserProfileTitlesModalProps {
  activeTitleName?: string
}

export const UserProfileTitlesModal = ({
  activeTitleName
}: UserProfileTitlesModalProps) => {
  const { closeModal } = useModalContext()
  const { records: titles, isLoading, isError } = useUserTitles()
  const [query, setQuery] = useState('')

  const sortedTitles = useMemo(() => {
    return [...titles].sort((a, b) => b.points - a.points)
  }, [titles])

  const filteredTitles = useMemo(() => {
    if (!query) return sortedTitles
    const normalizedQuery = query.trim().toLowerCase()
    return sortedTitles.filter(title =>
      title.name.toLowerCase().includes(normalizedQuery)
    )
  }, [query, sortedTitles])

  const hasTitles = sortedTitles.length > 0
  const hasFilteredResults = filteredTitles.length > 0

  const renderContent = () => {
    if (isError) {
      return (
        <p className="rounded-2xl border border-dashed border-[#E0D7FF] bg-white/80 px-4 py-6 text-center text-sm text-[#6B6883]">
          Não foi possível carregar seus títulos
        </p>
      )
    }

    if (isLoading && !hasTitles) {
      return (
        <p className="rounded-2xl border border-dashed border-[#E0D7FF] bg-white/80 px-4 py-6 text-center text-sm text-[#6B6883]">
          Carregando títulos...
        </p>
      )
    }

    if (!hasTitles) {
      return (
        <p className="rounded-2xl border border-dashed border-[#E0D7FF] bg-white/80 px-4 py-6 text-center text-sm text-[#6B6883]">
          Você ainda não conquistou títulos
        </p>
      )
    }

    if (!hasFilteredResults) {
      return (
        <p className="rounded-2xl border border-dashed border-[#E0D7FF] bg-white/80 px-4 py-6 text-center text-sm text-[#6B6883]">
          Nenhum título encontrado
        </p>
      )
    }

    return (
      <div className="space-y-3 max-h-[280px] overflow-y-auto pr-1">
        {filteredTitles.map(title => {
          const isActive =
            Boolean(activeTitleName) && title.name === activeTitleName

          return (
            <div
              key={`${title.name}-${title.points}`}
              className={cn(
                'flex items-center justify-between rounded-xl border border-[#E9E7FF] bg-white px-4 py-3 shadow-sm transition-colors',
                isActive
                  ? 'border-[#FFD8BC] bg-[#FFF5ED] text-[#C75300]'
                  : 'bg-white text-[#1F1C2B]'
              )}
            >
              <span className="font-semibold text-lg leading-tight">
                {title.name}
                {isActive && (
                  <span className="ml-2 text-xs font-semibold uppercase text-[#C75300]">
                    Atual
                  </span>
                )}
              </span>
              <span className="text-sm text-[#7F7FAD]">{title.points} pts</span>
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <Modal asCard>
      <ModalHeader>
        <ModalTitle>Títulos</ModalTitle>
      </ModalHeader>
      <ModalBody className="space-y-4">
        <div className="relative">
          <input
            type="search"
            placeholder="Nome do título"
            value={query}
            onChange={event => setQuery(event.target.value)}
            disabled={isLoading || !hasTitles}
            className={cn(
              inputClassName,
              'pr-10',
              (isLoading || !hasTitles) && 'opacity-50 cursor-not-allowed'
            )}
            aria-label="Buscar títulos"
          />
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-[#7F7FAD]" />
        </div>

        {renderContent()}
      </ModalBody>
      <ModalFooter>
        <Button variant="primary" className="w-full" onClick={closeModal}>
          Fechar
        </Button>
      </ModalFooter>
    </Modal>
  )
}
